/**
 * Long `.in("col", values)` lists are sent in the request URL. A few hundred
 * IDs makes the URL too long and the request fails (often silently). Every
 * query that filters by a list whose size depends on data must go through
 * `inChunks` / `inChunksAll` so it is split into small batches.
 */
export const IN_CHUNK_SIZE = 100;
const PAGE_SIZE = 1000;
const CONCURRENCY = 4;

type Result<T> = { data: T[] | null; error: any };

export function chunkArray<T>(values: readonly T[], size = IN_CHUNK_SIZE): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < values.length; i += size) out.push(values.slice(i, i + size));
  return out;
}

function unique<V>(values: readonly V[]): V[] {
  return Array.from(new Set(values.filter((v) => v !== null && v !== undefined && (v as any) !== "")));
}

async function runLimited<T>(tasks: Array<() => Promise<T>>, limit = CONCURRENCY): Promise<T[]> {
  const results: T[] = new Array(tasks.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
    while (next < tasks.length) {
      const i = next++;
      results[i] = await tasks[i]();
    }
  });
  await Promise.all(workers);
  return results;
}

/**
 * Runs `query` once per batch of values and merges the rows. Returns the same
 * `{ data, error }` shape as a normal query, so it drops in where `.in()` was.
 * Works for selects and for update/delete calls (data is then empty).
 * Note: ordering/limit apply per batch — re-sort the merged rows if needed.
 */
export async function inChunks<V, T = any>(
  values: readonly V[],
  query: (chunk: V[]) => PromiseLike<Result<T> | { error: any; data?: any }>,
  size = IN_CHUNK_SIZE,
): Promise<{ data: T[]; error: any }> {
  const list = unique(values);
  if (list.length === 0) return { data: [], error: null };
  try {
    const parts = await runLimited(
      chunkArray(list, size).map((chunk) => async () => {
        const res = await query(chunk);
        if (res.error) throw res.error;
        return (Array.isArray(res.data) ? res.data : []) as T[];
      }),
    );
    return { data: parts.flat(), error: null };
  } catch (error) {
    return { data: [], error };
  }
}

/** Like `inChunks`, but also pages each batch past the 1000-row limit. Throws on error. */
export async function inChunksAll<V, T = any>(
  values: readonly V[],
  query: (chunk: V[], from: number, to: number) => PromiseLike<Result<T>>,
  size = IN_CHUNK_SIZE,
): Promise<T[]> {
  const list = unique(values);
  if (list.length === 0) return [];
  const parts = await runLimited(
    chunkArray(list, size).map((chunk) => async () => {
      const rows: T[] = [];
      for (let from = 0; ; from += PAGE_SIZE) {
        const { data, error } = await query(chunk, from, from + PAGE_SIZE - 1);
        if (error) throw error;
        const batch = data ?? [];
        rows.push(...batch);
        if (batch.length < PAGE_SIZE) break;
      }
      return rows;
    }),
  );
  return parts.flat();
}
